(() => {
  const SELECTORS = {
    menu: '[data-ca-mobile-menu]',
    drawer: '[data-ca-menu-drawer]',
    openButton: '.m-menu-button',
    closeButton: '[data-ca-menu-close]',
    openPanel: '[data-ca-menu-open-panel]',
    backButton: '[data-ca-menu-back]',
    panels: '[data-ca-menu-panel]'
  };

  class CAMobileMenu {
    constructor(root) {
      /*
       * The menu is originally rendered inside the header.
       * Sticky/transformed header ancestors can trap position: fixed,
       * causing the overlay to cover only the header.
       *
       * Move the complete menu root directly under <body> so it is
       * positioned relative to the actual viewport.
       */
      if (root.parentElement !== document.body) {
        document.body.appendChild(root);
      }

      this.root = root;
      this.drawer = root.querySelector(SELECTORS.drawer);
      this.openButtons = [
        ...document.querySelectorAll(SELECTORS.openButton)
      ];
      this.closeButtons = [
        ...root.querySelectorAll(SELECTORS.closeButton)
      ];
      this.panels = [
        ...root.querySelectorAll(SELECTORS.panels)
      ];

      this.panelStack = ['root'];
      this.lastFocusedElement = null;
      this.scrollPosition = 0;
      this.isOpen = false;

      if (!this.drawer || !this.openButtons.length) return;

      this.handleKeydown = this.handleKeydown.bind(this);
      this.preventBackgroundTouch = this.preventBackgroundTouch.bind(this);

      this.bindEvents();
      this.resetPanels();
    }

    bindEvents() {
      this.openButtons.forEach((button) => {
        button.style.pointerEvents = 'auto';
        button.setAttribute('role', 'button');
        button.setAttribute('tabindex', '0');
        button.setAttribute('aria-controls', this.root.id);
        button.setAttribute('aria-expanded', 'false');

        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.open();
        });

        button.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.open();
          }
        });
      });

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.close();
        });
      });

      this.root.addEventListener('click', (event) => {
        const panelTrigger = event.target.closest(
          SELECTORS.openPanel
        );

        if (panelTrigger) {
          const panelId = panelTrigger.getAttribute(
            'data-ca-menu-open-panel'
          );

          this.showPanel(panelId, panelTrigger);
          return;
        }

        const backButton = event.target.closest(
          SELECTORS.backButton
        );

        if (backButton) {
          this.goBack();
        }
      });
    }

    open() {
      if (this.isOpen) return;

      this.isOpen = true;
      this.lastFocusedElement = document.activeElement;
      this.scrollPosition =
        window.scrollY ||
        document.documentElement.scrollTop ||
        0;

      this.root.classList.add('is-open');
      this.root.setAttribute('aria-hidden', 'false');

      /*
       * position: fixed on body is more reliable on mobile Safari
       * than overflow: hidden by itself.
       */
      document.documentElement.classList.add(
        'ca-mobile-menu-open'
      );

      document.body.classList.add(
        'ca-mobile-menu-open'
      );

      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';

      this.openButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'true');

        const hamburger = button.querySelector(
          '.m-hamburger-box'
        );

        if (hamburger) {
          hamburger.classList.add('active');
        }
      });

      document.addEventListener(
        'keydown',
        this.handleKeydown
      );

      document.addEventListener(
        'touchmove',
        this.preventBackgroundTouch,
        {
          passive: false
        }
      );

      requestAnimationFrame(() => {
        this.drawer.focus({
          preventScroll: true
        });
      });
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;

      this.root.classList.remove('is-open');
      this.root.setAttribute('aria-hidden', 'true');

      document.documentElement.classList.remove(
        'ca-mobile-menu-open'
      );

      document.body.classList.remove(
        'ca-mobile-menu-open'
      );

      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';

      /*
       * Return to the exact page position where the drawer opened.
       */
      window.scrollTo({
        top: this.scrollPosition,
        left: 0,
        behavior: 'instant'
      });

      this.openButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'false');

        const hamburger = button.querySelector(
          '.m-hamburger-box'
        );

        if (hamburger) {
          hamburger.classList.remove('active');
        }
      });

      document.removeEventListener(
        'keydown',
        this.handleKeydown
      );

      document.removeEventListener(
        'touchmove',
        this.preventBackgroundTouch
      );

      window.setTimeout(() => {
        this.resetPanels();
      }, 310);

      if (
        this.lastFocusedElement &&
        typeof this.lastFocusedElement.focus === 'function'
      ) {
        this.lastFocusedElement.focus({
          preventScroll: true
        });
      }
    }

    preventBackgroundTouch(event) {
      /*
       * Permit scrolling only inside the drawer's designated
       * scrollable menu areas.
       */
      const scrollArea = event.target.closest(
        '.ca-mobile-menu__scroll'
      );

      if (!scrollArea) {
        event.preventDefault();
      }
    }

    showPanel(panelId, trigger) {
      const nextPanel = this.root.querySelector(
        `[data-ca-menu-panel="${CSS.escape(panelId)}"]`
      );

      const currentPanel = this.getCurrentPanel();

      if (
        !nextPanel ||
        !currentPanel ||
        nextPanel === currentPanel
      ) {
        return;
      }

      currentPanel.classList.remove('is-active');
      currentPanel.classList.add('is-behind');
      currentPanel.setAttribute('aria-hidden', 'true');

      nextPanel.classList.remove('is-behind');
      nextPanel.classList.add('is-active');
      nextPanel.setAttribute('aria-hidden', 'false');

      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }

      this.panelStack.push(panelId);

      const firstFocusable = nextPanel.querySelector(
        'button, a, input'
      );

      if (firstFocusable) {
        window.setTimeout(() => {
          firstFocusable.focus({
            preventScroll: true
          });
        }, 280);
      }
    }

    goBack() {
      if (this.panelStack.length <= 1) return;

      const currentId = this.panelStack.pop();
      const previousId =
        this.panelStack[this.panelStack.length - 1];

      const currentPanel = this.root.querySelector(
        `[data-ca-menu-panel="${CSS.escape(currentId)}"]`
      );

      const previousPanel = this.root.querySelector(
        `[data-ca-menu-panel="${CSS.escape(previousId)}"]`
      );

      if (!currentPanel || !previousPanel) return;

      currentPanel.classList.remove('is-active');
      currentPanel.setAttribute('aria-hidden', 'true');

      previousPanel.classList.remove('is-behind');
      previousPanel.classList.add('is-active');
      previousPanel.setAttribute('aria-hidden', 'false');

      const originalTrigger = this.root.querySelector(
        `[data-ca-menu-open-panel="${CSS.escape(currentId)}"]`
      );

      if (originalTrigger) {
        originalTrigger.setAttribute(
          'aria-expanded',
          'false'
        );

        window.setTimeout(() => {
          originalTrigger.focus({
            preventScroll: true
          });
        }, 260);
      }
    }

    getCurrentPanel() {
      const currentId =
        this.panelStack[this.panelStack.length - 1];

      return this.root.querySelector(
        `[data-ca-menu-panel="${CSS.escape(currentId)}"]`
      );
    }

    resetPanels() {
      this.panelStack = ['root'];

      this.panels.forEach((panel) => {
        const isRoot =
          panel.getAttribute('data-ca-menu-panel') ===
          'root';

        panel.classList.toggle('is-active', isRoot);
        panel.classList.remove('is-behind');

        panel.setAttribute(
          'aria-hidden',
          isRoot ? 'false' : 'true'
        );
      });

      this.root
        .querySelectorAll(SELECTORS.openPanel)
        .forEach((trigger) => {
          trigger.setAttribute(
            'aria-expanded',
            'false'
          );
        });
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();

        if (this.panelStack.length > 1) {
          this.goBack();
        } else {
          this.close();
        }

        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = [
        ...this.drawer.querySelectorAll(
          [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
          ].join(', ')
        )
      ].filter((element) => {
        return element.offsetParent !== null;
      });

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const initialize = () => {
    document
      .querySelectorAll(SELECTORS.menu)
      .forEach((menu) => {
        if (
          menu.dataset.caMenuInitialized === 'true'
        ) {
          return;
        }

        menu.dataset.caMenuInitialized = 'true';

        new CAMobileMenu(menu);
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    initialize
  );
})();